import { useMemo } from "react";
import type { AccountState, SpotState } from "#/hooks/use-hyperliquid-account";
import { useHyperSpotBalances } from "#/hooks/use-hyperliquid-account";
import { useUnifiedBalance } from "#/hooks/use-unified-balance";
import { toNumber } from "#/lib/formatters";
import { useTradeAccount } from "../../../hooks/use-trade-data.ts";

type SpotBalance = SpotState["balances"][number];

/**
 * Synthesize the USDC row in the balances list to reflect the user's full
 * USDC holdings.
 *
 * Unified accounts behave in one of two HL-internal accounting modes:
 *   - Model A: position margin held inside spot — `spot.total` already
 *     includes the locked collateral, `spot.hold == perpHold`.
 *   - Model B: position margin transferred to perp side — `spot.total`
 *     reflects only the spot-side residual, `spot.hold = 0`.
 *
 * `perpAV + spot.total - spot.hold` adapts to both: in Model A the
 * subtraction cancels the double-count (since perpAV ≈ spot.hold − loss);
 * in Model B `spot.hold = 0` so the formula reduces to `perpAV + spot.total`.
 * This is the same shape as the equity formula in trading-balance displays,
 * keeping balances row and equity cell in sync.
 *
 * Hold = `perpAV` (positions MTM) makes `total - hold = spot - spot.hold`,
 * which is the free spot portion — the cleanest "available to trade"
 * semantic. PnL is intentionally excluded from `available` because paper
 * gains aren't usable as new-position margin.
 *
 * Non-unified (default + dexAbstraction): spot and perp are separate
 * pools, so total/hold add the two. For dexAbstraction, mergeAcrossDexes
 * already summed main + xyz into perpAccountValue / perpHold.
 *
 * See ACCOUNT.md → Balance display rules.
 */
export function mergePerpIntoSpotUsdc({
	spotBalances,
	accountState,
	isUnified,
}: {
	spotBalances: readonly SpotBalance[];
	accountState: AccountState | undefined;
	isUnified: boolean;
}): SpotBalance[] {
	if (!accountState) return [...spotBalances];

	const perpAccountValue = toNumber(
		accountState.marginSummary?.accountValue ??
			accountState.crossMarginSummary.accountValue,
	);
	const perpHold = toNumber(
		accountState.marginSummary?.totalMarginUsed ??
			accountState.crossMarginSummary.totalMarginUsed,
	);

	const spotUsdcIdx = spotBalances.findIndex((b) => b.coin === "USDC");
	const spotUsdc = spotUsdcIdx >= 0 ? spotBalances[spotUsdcIdx] : undefined;
	const spotUsdcTotal = spotUsdc ? toNumber(spotUsdc.total) : 0;
	const spotUsdcHold = spotUsdc ? toNumber(spotUsdc.hold) : 0;

	const mergedTotal = isUnified
		? perpAccountValue + spotUsdcTotal - spotUsdcHold
		: spotUsdcTotal + perpAccountValue;
	const mergedHold = isUnified
		? Math.max(0, perpAccountValue)
		: spotUsdcHold + perpHold;

	const mergedUsdc: SpotBalance = {
		coin: "USDC",
		// SDK 0.33.1: balances is a union — `+`-prefixed synthetic coins carry
		// no `token`; narrow before reading it.
		token: spotUsdc && "token" in spotUsdc ? spotUsdc.token : 0,
		total: String(mergedTotal),
		hold: String(mergedHold),
		entryNtl: "0",
	};

	if (spotUsdcIdx >= 0) {
		const result = [...spotBalances];
		result[spotUsdcIdx] = mergedUsdc;
		return result;
	}
	return [mergedUsdc, ...spotBalances];
}

export function useEffectiveBalances() {
	const spot = useHyperSpotBalances();
	const account = useTradeAccount();
	const unified = useUnifiedBalance();

	const balances = useMemo(
		() =>
			mergePerpIntoSpotUsdc({
				spotBalances: spot.balances,
				accountState: account.accountState,
				isUnified: unified.isUnified,
			}),
		[spot.balances, account.accountState, unified.isUnified],
	);

	return {
		balances,
		isError: spot.isError || account.isError || unified.isError,
		refetch: () => {
			spot.refetch();
			account.refetch();
			unified.refetch();
		},
	};
}
